import { API_URL } from "./config";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import * as pdfjsLib from "pdfjs-dist";

// ✅ Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

// Client-side id so each placement marker has a stable React key.
// It is UI-only and is removed again before placements are sent to the backend.
let nextPlacementId = 0;
const withPlacementId = (placement) => ({ ...placement, _key: nextPlacementId++ });
const withoutPlacementId = ({ _key, ...placement }) => placement;

const SenderPlacement = () => {
  const { token } = useParams();
  const [pdfUrl, setPdfUrl] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [recipients, setRecipients] = useState([]);
  const [activeRecipient, setActiveRecipient] = useState("");
  const [placements, setPlacements] = useState([]);
  const canvasRefs = useRef({});

  // ✅ Fetch PDF file
  useEffect(() => {
    fetch(`${API_URL}/api/get-document/${token}`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch PDF");
        return res.blob();
      })
      .then((blob) => setPdfUrl(URL.createObjectURL(blob)))
      .catch((err) => console.error("Error fetching PDF:", err));
  }, [token]);

  // ✅ Render PDF pages safely
  useEffect(() => {
    if (!pdfUrl) return;

    const loadingTask = pdfjsLib.getDocument(pdfUrl);
    loadingTask.promise
      .then(async (pdf) => {
        setNumPages(pdf.numPages);
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const canvas = canvasRefs.current[i];
          if (!canvas) {
            console.warn(`Canvas for page ${i} not yet available`);
            continue;
          }

          const viewport = page.getViewport({ scale: 1.2 });
          const context = canvas.getContext("2d");
          canvas.width = viewport.width;
          canvas.height = viewport.height;

          const renderContext = { canvasContext: context, viewport };
          page.render(renderContext);
        }
      })
      .catch((err) => console.error("Error rendering PDF:", err));
  }, [pdfUrl]);

  // ✅ Fetch Recipients / Signers
  useEffect(() => {
    fetch(`${API_URL}/api/get-signers/${token}`)
      .then((res) => res.json())
      .then((data) => {
        console.log("📩 Signers API response:", data);

        // Support both "signers" and "recipients" key from backend
        const signerList = data.signers || data.recipients || [];

        setRecipients(signerList);
        if (signerList.length > 0) {
          setActiveRecipient(signerList[0].email);
        }
      })
      .catch((err) => console.error("Error loading signers:", err));
  }, [token]);

  // ✅ Fetch saved placements
  useEffect(() => {
    fetch(`${API_URL}/api/get-placements/${token}`)
      .then((res) => res.json())
      .then((data) => setPlacements((data.placements || []).map(withPlacementId)))
      .catch((err) => console.error("Error loading placements:", err));
  }, [token]);

  // ✅ Handle click on PDF to add placement
  const handleCanvasClick = (e, pageNum) => {
    if (!activeRecipient) {
      alert("Please select a recipient first");
      return;
    }

    const canvas = canvasRefs.current[pageNum];
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const xPct = x / canvas.width;
    const yPct = y / canvas.height;

    const newPlacement = withPlacementId({ page: pageNum, xPct, yPct, recipient: activeRecipient });
    setPlacements((prev) => [...prev, newPlacement]);
  };

  // ✅ Save placements to backend
  const handleSave = async () => {
    try {
      const res = await fetch(`${API_URL}/api/save-placements/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ placements: placements.map(withoutPlacementId) }),
      });

      const data = await res.json();
      if (res.ok) {
        alert("✅ Placements saved successfully!");
      } else {
        alert("❌ Failed: " + (data.error || "Unknown error"));
      }
    } catch (err) {
      console.error(err);
      alert("❌ Server error");
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-xl font-bold mb-4 text-black">Signature Placement</h2>

      {/* ✅ Recipient Selector */}
      <div className="mb-4">
        <label className="block font-medium text-black mb-1">Select Recipient:</label>
        {recipients.length > 0 ? (
          <select
            value={activeRecipient}
            onChange={(e) => setActiveRecipient(e.target.value)}
            className="border rounded p-2 mt-1 w-full text-black"
          >
            {recipients.map((r) => (
              <option key={r.email} value={r.email}>
                {r.email}
              </option>
            ))}
          </select>
        ) : (
          <p className="text-gray-500 italic">
            No recipients found. Please ensure your backend returns a list of signers.
          </p>
        )}
      </div>
       
      {/* ✅ Render all PDF pages */}
      {pdfUrl ? (
        <div className="space-y-6">
          {Array.from({ length: numPages }).map((_, i) => {
            const pageNum = i + 1;
            return (
              <div
                key={pageNum}
                className="relative border rounded shadow inline-block bg-gray-50"
              >
                <canvas
                  ref={(el) => (canvasRefs.current[pageNum] = el)}
                  onClick={(e) => handleCanvasClick(e, pageNum)}
                  className="border cursor-crosshair"
                />

                {/* Signature placements overlay */}
                {placements
                  .filter((p) => p.page === pageNum)
                  .map((p) => (
                    <div
                      key={p._key}
                      className="absolute border-2 border-blue-500 bg-blue-100 text-xs px-2 py-1 rounded cursor-pointer"
                      style={{
                        left: `${p.xPct * (canvasRefs.current[pageNum]?.width || 0)}px`,
                        top: `${p.yPct * (canvasRefs.current[pageNum]?.height || 0)}px`,
                        transform: "translate(-50%, -50%)",
                      }}
                    >
                      ✍ {p.recipient}
                    </div>
                  ))}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-red-500 mt-4">❌ Could not load PDF</p>
      )}

      <button
        onClick={handleSave}
        className="mt-6 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
      >
        Save Placements
      </button>
    </div>
  );
};

export default SenderPlacement;